import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Stack,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ErrorContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2),
  textAlign: 'center',
  maxWidth: 600,
  marginLeft: 'auto',
  marginRight: 'auto',
  border: `1px solid ${theme.palette.error.light}`,
}));

const ErrorDetails = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  textAlign: 'left',
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  fontFamily: 'monospace',
  fontSize: '0.875rem',
}));

class CampaignErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Campaign Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Log to error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  logErrorToService = (error, errorInfo) => {
    // TODO: Integrate with error reporting service (Sentry, LogRocket, etc.)
    console.log('Would log to error service:', { error, errorInfo });
  };

  handleRetry = () => {
    const { onRetry } = this.props;
    const newRetryCount = this.state.retryCount + 1;
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: newRetryCount,
    });

    // Call parent component's retry function if provided
    if (onRetry) {
      onRetry(newRetryCount);
    }
  };

  handleToggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  getErrorCategory = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';
    
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return 'network';
    }
    if (errorMessage.includes('authentication') || errorMessage.includes('unauthorized')) {
      return 'auth';
    }
    if (errorMessage.includes('validation') || errorMessage.includes('invalid')) {
      return 'validation';
    }
    if (errorMessage.includes('rate limit') || errorMessage.includes('quota')) {
      return 'rate_limit';
    }
    return 'unknown';
  };

  getErrorSuggestions = (category) => {
    const suggestions = {
      network: [
        'Check your internet connection',
        'Verify the API server is running',
        'Try refreshing the page',
        'Check if you\'re behind a firewall or VPN',
      ],
      auth: [
        'Please log in again',
        'Check if your session has expired',
        'Verify your account permissions',
        'Contact support if the problem persists',
      ],
      validation: [
        'Check the data you entered',
        'Ensure all required fields are filled',
        'Verify URLs are valid and accessible',
        'Try different input values',
      ],
      rate_limit: [
        'Wait a few minutes before trying again',
        'Check your usage quota',
        'Contact support for quota increases',
        'Spread out your requests over time',
      ],
      unknown: [
        'Try refreshing the page',
        'Check the browser console for more details',
        'Contact support with the error details',
        'Try using a different browser',
      ],
    };

    return suggestions[category] || suggestions.unknown;
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const category = this.getErrorCategory(error);
      const suggestions = this.getErrorSuggestions(category);
      const { fallbackComponent, showRetry = true, maxRetries = 3 } = this.props;

      // If a custom fallback component is provided, use it
      if (fallbackComponent && this.state.retryCount < maxRetries) {
        return fallbackComponent(error, this.handleRetry);
      }

      return (
        <ErrorContainer elevation={3}>
          <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          
          <Typography variant="h5" component="h1" gutterBottom>
            Campaign Error
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            Something went wrong with the campaign system. We're sorry for the inconvenience.
          </Typography>

          {error?.message && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Error:</strong> {error.message}
              </Typography>
            </Alert>
          )}

          <Stack spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="info" />
              Suggestions
            </Typography>
            
            <List dense>
              {suggestions.map((suggestion, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={suggestion}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
            {showRetry && this.state.retryCount < maxRetries && (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                color="primary"
              >
                Try Again ({maxRetries - this.state.retryCount} attempts left)
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </Stack>

          {process.env.NODE_ENV === 'development' && (
            <Box>
              <Button
                startIcon={this.state.showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={this.handleToggleDetails}
                size="small"
                color="info"
              >
                {this.state.showDetails ? 'Hide' : 'Show'} Technical Details
              </Button>
              
              <Collapse in={this.state.showDetails}>
                <ErrorDetails>
                  <Typography variant="h6" gutterBottom>
                    <BugIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Error Details (Development Mode)
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Error Message:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {this.state.error?.toString()}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Component Stack:
                  </Typography>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {this.state.errorInfo?.componentStack}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Error Stack:
                  </Typography>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {this.state.error?.stack}
                  </Typography>
                </ErrorDetails>
              </Collapse>
            </Box>
          )}

          <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
            If this problem persists, please contact support with the error details above.
          </Typography>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default CampaignErrorBoundary; 